import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_VideoEnhancer = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/video-enhancer',
			name: 'Video Enhancer — Enhance Video Quality Online | Lift',
			description:
				"Improve video quality in one step with Lift's AI video enhancer — upload a clip and get sharper detail, balanced lighting, and richer color without changing the file size. Try Lift now.",
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/video-enhancer#product' },
				{ '@id': 'https://liftapp.ai/video-enhancer#howto' },
				{ '@id': 'https://liftapp.ai/video-enhancer#benefits' },
				{ '@id': 'https://liftapp.ai/video-enhancer#faq' },
				{ '@id': 'https://liftapp.ai/video-enhancer#why-lift' },
				{ '@id': 'https://liftapp.ai/video-enhancer#explore-tools' },
				{ '@id': 'https://liftapp.ai/video-enhancer#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/video-enhancer#product',
			name: 'Video Enhancer Online',
			description:
				"Enhance video quality with AI. Upload a clip and Lift sharpens detail, balances lighting, and enriches color in every frame while keeping the original file size. Try Lift's video enhancer now!",
			url: 'https://liftapp.ai/video-enhancer',
			fileFormat: [
				'video/mp4',
				'video/quicktime',
				'video/x-msvideo',
				'video/webm',
				'video/x-matroska',
				'video/mpeg',
			],
			memoryRequirements: 'Max file size: 500MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/video-enhancer#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your Video',
					text: 'Drop in a clip from your device — MP4, MOV, AVI, MKV, WEBM, and many other formats are supported.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let AI Enhance It',
					text: 'Enhancement starts automatically: Lift analyzes the footage, sharpens details, and balances brightness and color in every frame.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Compare And Retry',
					text: 'Play the result in the built-in player and flip between the original and enhanced version, or tap Retry for a fresh pass.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download Your Result',
					text: 'Save the enhanced video to your device — same format, same file size, better picture.',
				},
			],
		},
		// benefits
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-enhancer#benefits',
			name: 'Who can benefit from Video Enhancer?',
			description:
				'Lift Video Enhancer helps content creators, small businesses, video editors, teachers, and families sharpen and brighten any clip in one AI pass.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Phone footage shot indoors or at dusk often comes out soft and dull. Increase video quality before posting so reels, shorts, and vlogs look polished on any feed.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Small businesses and marketers — Product demos and promo clips sell better when they are crisp and well lit. Lift works as a video sharpener and color corrector in one pass, no editing suite required.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Video editors — Fixing exposure and softness shot by shot eats up hours. Run raw clips through Lift first and start your edit from footage that already looks clean.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Teachers and course creators — Lessons recorded on a webcam or in a poorly lit room are hard to follow. Brighter, clearer video keeps students focused on the material, not the noise.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Families and travelers — Shaky hands and bad lighting should not ruin a memory. Unblur video from birthdays, trips, and family gatherings and keep the moments looking the way you remember them.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.4f82ac71.webp',
				name: 'Video enhancer tool',
				description: 'Video enhancer — Lift',
			},
		},
		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/video-enhancer#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Video Enhancer?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Video Enhancer is Lift's AI video tool that makes existing footage look better. It analyzes your clip frame by frame, sharpens fine detail, and corrects brightness, contrast, and color — turning dim or blurry video into a clear, natural-looking result without any manual adjustments.",
					},
				},
				{
					'@type': 'Question',
					name: 'How do I enhance video quality online?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload your video to Lift and the enhancement begins on its own — there are no settings to configure. Once the AI finishes, preview the clip in the player and download it, or tap Retry to generate a new version.',
					},
				},
				{
					'@type': 'Question',
					name: "Does Video Enhancer increase my video's resolution or file size?",
					acceptedAnswer: {
						'@type': 'Answer',
						text: "No. Video Enhancer improves how your footage looks — sharpness, lighting, and color — while keeping the original resolution and file size, so the result is just as easy to store and share. If you need to make a video larger or convert it to HD, Lift's Video Upscaler is built for that.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All videos enhanced in Lift can be exported without a watermark when you're on the Pro plan. Download them in the original format — ready for social media, ads, or your next edit.",
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my videos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your videos securely and uses them only to generate your results.',
					},
				},
			],
		},
		// why-lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-enhancer#why-lift',
			name: 'Why Lift Video Enhancer',
			description:
				'Discover the advantages of Lift Video Enhancer: high-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description:
						'Available on iOS, Android, and Web for seamless access.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyLift.7c19de84.webp',
				name: 'Why use Lift Video Enhancer',
				description: 'Why use Lift Video Enhancer — Lift.',
			},
		},
		// explore-tools
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/video-enhancer#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		// all-in-one
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/video-enhancer#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Video Enhancer',
					item: 'https://liftapp.ai/video-enhancer',
				},
			],
		},
	],
};
